import { useEffect, useState } from 'react';
import { SponsorsService } from './PartnerTasksService';
import type { TaskDetails } from './adapters/subgramAdapter';
import PartnerTaskItem from '../../../components/PartnerTaskItem/PartnerTaskItem';
import Loader from '../../../components/Loader/Loader';
import styles from './PartnerTasks.module.css';
import { useModalStore } from '../../../store/ModalStore';

interface PartnerTasksProps {
  onLoadingChange?: (isLoading: boolean) => void;
}

const PartnerTasks = ({ onLoadingChange }: PartnerTasksProps) => {
  const [partnerTasks, setPartnerTasks] = useState<TaskDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const { setCurrentTask, setOpen } = useModalStore();

  // Функция для сортировки заданий - CLAIMED задания в конец
  const sortTasks = (tasks: TaskDetails[]): TaskDetails[] => {
    const statusPriority: Record<string, number> = {
      DONE: 1,
      NOT_STARTED: 2,
      IN_PROGRESS: 3,
      WAITING: 4,
      CLAIMED: 5, // CLAIMED задания идут в самый конец
    };

    return [...tasks].sort((a, b) => {
      const aPriority = statusPriority[a.status] || 2;
      const bPriority = statusPriority[b.status] || 2;
      return aPriority - bPriority;
    });
  };

  useEffect(() => {
    console.log('PartnerTasks: Начинаем загрузку');
    setIsLoading(true);
    onLoadingChange?.(true);

    const fetchTasks = async () => {
      try {
        const tasks = await SponsorsService.getSponsorTasks();
        console.log('PartnerTasks: Партнёрские задачи загружены:', tasks);
        const sortedTasks = sortTasks(tasks);
        setPartnerTasks(sortedTasks);
      } catch (error) {
        console.error(
          'PartnerTasks: Ошибка получения партнёрских задач:',
          error,
        );
      } finally {
        setIsLoading(false);
        onLoadingChange?.(false);
        console.log('PartnerTasks: Загрузка завершена');
      }
    };
    fetchTasks();

    // Слушаем событие завершения задачи
    const handleTaskCompleted = (event: CustomEvent) => {
      const { taskId } = event.detail;
      setCompletedTasks((prev) => new Set([...prev, taskId]));

      // Обновляем список задач
      setPartnerTasks((prevTasks) => {
        const updatedTasks = prevTasks.map((task) =>
          task.id === taskId
            ? { ...task, status: 'CLAIMED' as const, buttonText: 'Выполнено' }
            : task,
        );
        return sortTasks(updatedTasks);
      });
    };

    window.addEventListener(
      'taskCompleted',
      handleTaskCompleted as EventListener,
    );

    return () => {
      window.removeEventListener(
        'taskCompleted',
        handleTaskCompleted as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Убираем зависимость от onLoadingChange чтобы избежать бесконечных перерендеров

  const handleTaskClick = (task: TaskDetails) => {
    console.log('Клик по партнёрской задаче:', task);
    setCurrentTask(task);
    setOpen(true);
  };

  return (
    <div className={styles.partnerTasksContainer}>
      {isLoading ? (
        <div className={styles.loading}>
          <Loader />
        </div>
      ) : null}
      {partnerTasks.length > 0 && (
        <>
          <div className={styles.partnerTasksHeader}>Партнёрские</div>
          <div className={styles.partnerTasksList}>
            {partnerTasks.map((task) => {
              let buttonText = task.buttonText;
              if (completedTasks.has(task.id) || task.status === 'CLAIMED') {
                buttonText = 'Выполнено';
              }

              return (
                <PartnerTaskItem
                  key={task.id}
                  task={task}
                  onClick={() => handleTaskClick(task)}
                  buttonText={buttonText}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PartnerTasks;
